"use client";

import { useCallback, useMemo, useState } from "react";
import type { ChangeEvent, CSSProperties } from "react";
import { AnimatePresence, motion, useReducedMotion } from "motion/react";
import type { Variants } from "motion/react";
import { AedSymbol } from "@/components/ui/AedSymbol";
import { buttonVariants } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import {
  GOLD_HISTORY_USD_PER_OZ,
  GOLD_RATE_AED_PER_GRAM,
  SILVER_HISTORY_USD_PER_OZ,
  SILVER_RATE_AED_PER_GRAM,
} from "@/lib/rates";
import { useLiveRatesContext } from "@/components/providers/LiveRatesProvider";

const MIN_AMOUNT = 10;
const MAX_AMOUNT = 5000;
const START_OPTIONS = [
  { key: "2016", label: "July 2016", years: 10 },
  { key: "2019", label: "July 2019", years: 7 },
  { key: "2021", label: "July 2021", years: 5 },
] as const;
type StartKey = (typeof START_OPTIONS)[number]["key"];
const GOAL_MILESTONES = [
  { amount: 2_000, emoji: "📱", name: "A phone" },
  { amount: 8_000, emoji: "✈️", name: "A trip" },
  { amount: 20_000, emoji: "💍", name: "A wedding" },
  { amount: 80_000, emoji: "🚗", name: "A car" },
  { amount: 250_000, emoji: "🎓", name: "Tuition" },
  { amount: 500_000, emoji: "🏡", name: "A home" },
  { amount: 800_000, emoji: "🌴", name: "Retirement" },
];

function fadeUp(reduce: boolean): Variants {
  return {
    hidden: { opacity: 0, y: reduce ? 0 : 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.55, ease: [0.2, 0, 0, 1] } },
  };
}

function FlippingNumber({ value }: { value: number }) {
  const reduce = useReducedMotion() ?? false;
  const formattedValue = Math.round(value).toLocaleString("en-US");

  return (
    <span role="text" aria-label={formattedValue}>
      <span className="inline-flex items-baseline tabular-nums" aria-hidden="true">
        {formattedValue.split("").map((character, index) => {
          const isDigit = /\d/.test(character);

          if (!isDigit) {
            return <span key={`${character}-${index}`} className="inline-block w-[0.26em] text-center">{character}</span>;
          }

          return (
            <span key={index} className="relative inline-block h-[0.92em] w-[0.64em] overflow-hidden">
              <AnimatePresence mode="popLayout" initial={false}>
                <motion.span
                  key={`${character}-${index}`}
                  className="absolute inset-0"
                  initial={{ y: reduce ? "0%" : "72%", opacity: reduce ? 1 : 0 }}
                  animate={{ y: "0%", opacity: 1 }}
                  exit={{ y: reduce ? "0%" : "-72%", opacity: reduce ? 1 : 0 }}
                  transition={{ duration: reduce ? 0 : 0.34, ease: [0.2, 0, 0, 1] }}
                >
                  {character}
                </motion.span>
              </AnimatePresence>
            </span>
          );
        })}
      </span>
    </span>
  );
}

function getHistoricalSeries(metal: "gold" | "silver", liveGoldRate?: number) {
  return metal === "gold"
    ? { history: GOLD_HISTORY_USD_PER_OZ, rate: liveGoldRate ?? GOLD_RATE_AED_PER_GRAM }
    : { history: SILVER_HISTORY_USD_PER_OZ, rate: SILVER_RATE_AED_PER_GRAM };
}

function buildBars(monthly: number, years: number, metal: "gold" | "silver", liveGoldRate?: number) {
  const { history, rate } = getHistoricalSeries(metal, liveGoldRate);
  const latestUsd = history[history.length - 1];
  const quarterCount = Math.min(years * 4, history.length - 1);
  const historicalPrices = history
    .slice(history.length - 1 - quarterCount)
    .map((usd) => (usd / latestUsd) * rate);
  let units = 0;
  let contributed = 0;
  let previousMonths = 0;

  return historicalPrices.map((price, index) => {
    const months = Math.max(
      1,
      Math.round(((index + 1) / historicalPrices.length) * years * 12)
    );
    const contribution = monthly * (months - previousMonths);
    contributed += contribution;
    units += contribution / price;
    previousMonths = months;

    const total = units * rate;
    return {
      months,
      contributed,
      growth: Math.max(0, total - contributed),
      height: total,
      total,
      units,
    };
  });
}

function calculateHistoricalValue(monthly: number, years: number, metal: "gold" | "silver", liveGoldRate?: number) {
  const bars = buildBars(monthly, years, metal, liveGoldRate);
  const finalBar = bars[bars.length - 1];
  const { history, rate } = getHistoricalSeries(metal, liveGoldRate);
  const startIndex = history.length - 1 - Math.min(years * 4, history.length - 1);
  const startRate = (history[startIndex] / history[history.length - 1]) * rate;
  const gain = finalBar.total - finalBar.contributed;
  return {
    invested: finalBar.contributed,
    value: finalBar.total,
    gain,
    gainPct: (gain / finalBar.contributed) * 100,
    goldAccumulated: finalBar.units,
    startRate,
    currentRate: rate,
  };
}

function formatShortCurrency(value: number) {
  if (value >= 1_000_000) return `${(value / 1_000_000).toFixed(value >= 10_000_000 ? 0 : 1)}M`;
  if (value >= 1_000) return `${(value / 1_000).toFixed(value >= 100_000 ? 0 : 1)}K`;
  return Math.round(value).toLocaleString("en-US");
}

function formatWholeCurrency(value: number) {
  return Math.round(value).toLocaleString("en-US");
}

function formatBarPeriod(months: number) {
  if (months < 12) return `${months}M`;
  if (months % 12 === 0) return `${months / 12}Y`;
  return `${(months / 12).toFixed(1)}Y`;
}

function getTooltipPlacement(index: number, total: number, height: number) {
  const horizontal = index < 4 ? "left-0 translate-x-0" : index > total - 5 ? "right-0 translate-x-0" : "left-1/2 -translate-x-1/2";
  const vertical = height > 0.68 ? "top-0" : "bottom-[calc(100%+0.5rem)]";
  return `${vertical} ${horizontal}`;
}

function getGoalMilestones(bars: ReturnType<typeof buildBars>) {
  const reachedMilestones = GOAL_MILESTONES.filter((milestone) => bars.some((bar) => bar.total >= milestone.amount)).slice(-5);
  const maxValue = bars[bars.length - 1]?.height || 1;

  return reachedMilestones.map((milestone) => {
    const index = Math.max(0, bars.findIndex((bar) => bar.total >= milestone.amount));
    const bar = bars[index];
    return {
      ...milestone,
      label: formatShortCurrency(bar.total),
      left: `${Math.min(96, Math.max(4, (index / Math.max(1, bars.length - 1)) * 100))}%`,
      bottom: `${Math.min(88, Math.max(12, (bar.height / maxValue) * 100 + 8))}%`,
    };
  });
}

export function GoldCalculator() {
  const reduce = useReducedMotion() ?? false;
  const liveRates = useLiveRatesContext();
  const [monthly, setMonthly] = useState(250);
  const [startKey, setStartKey] = useState<StartKey>("2016");
  const startOption = START_OPTIONS.find((option) => option.key === startKey) ?? START_OPTIONS[0];
  const years = startOption.years;
  const metal = "gold" as const;
  const liveGoldRate = liveRates?.goldBuy;
  const results = useMemo(
    () => calculateHistoricalValue(monthly, years, metal, liveGoldRate),
    [monthly, years, metal, liveGoldRate]
  );
  const bars = useMemo(
    () => buildBars(monthly, years, metal, liveGoldRate),
    [monthly, years, metal, liveGoldRate]
  );
  const milestones = useMemo(() => getGoalMilestones(bars), [bars]);
  const monthlyPct = ((monthly - MIN_AMOUNT) / (MAX_AMOUNT - MIN_AMOUNT)) * 100;
  const handleMonthly = useCallback((event: ChangeEvent<HTMLInputElement>) => setMonthly(Number(event.target.value)), []);

  return (
    <motion.section
      className="relative overflow-visible bg-neutral-0 px-4 pb-16 pt-16 sm:px-6 sm:pb-20 sm:pt-20 lg:px-10 lg:pb-24 lg:pt-24"
      aria-labelledby="calculator-heading"
      initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.15 }}
      variants={{ hidden: {}, visible: { transition: { staggerChildren: 0.08, delayChildren: 0.04 } } }}
    >
      <motion.div variants={fadeUp(reduce)} className="mx-auto mb-10 max-w-[1280px]">
        <p className="mb-3 text-xs font-semibold uppercase tracking-[0.16em] text-primary-600">
          Looking back, not guessing forward
        </p>
        <h2
          id="calculator-heading"
          className="max-w-[1100px] text-[clamp(1.75rem,3.5vw,2.5rem)] font-semibold leading-[1.15] tracking-[-0.015em] text-primary-900"
        >
          What saving a little would have been worth
        </h2>
        <p className="mt-5 max-w-[760px] text-lg leading-[1.55] text-text-body">
          Gold has had good years and flat years. Here&apos;s what putting a set
          amount aside each month would have come to, based on actual Dubai
          gold rates.
        </p>
      </motion.div>
      <div className="relative mx-auto max-w-[1280px] overflow-visible rounded-card bg-accent-50/70 px-6 py-7 sm:px-8 sm:py-8 lg:px-12 lg:py-9">
        <div className="relative grid gap-8 lg:min-h-[440px] lg:grid-cols-[0.78fr_1.22fr] lg:gap-20">
          <motion.div variants={fadeUp(reduce)} className="flex flex-col">
            <div className="max-w-[520px]">
              <p className="whitespace-nowrap text-[clamp(0.95rem,2.4vw,1.125rem)] font-semibold leading-snug text-primary-800">
                If you&apos;d saved{" "}
                <span className="whitespace-nowrap">
                  <AedSymbol className="inline h-[0.85em] w-[0.85em]" label="AED " />
                  {monthly.toLocaleString("en-US")}
                </span>
                /month since {startOption.key}, it could be worth
              </p>

              <div className="mt-3 flex items-baseline gap-2 text-success">
                <AedSymbol className="h-[clamp(2.15rem,3.6vw,3.8rem)] w-[clamp(2.15rem,3.6vw,3.8rem)]" label="AED " />
                <span className="font-heading text-[clamp(2.75rem,3.84vw,3.625rem)] font-semibold leading-[0.92] tracking-normal">
                  <FlippingNumber value={results.value} />
                </span>
              </div>

              <div className="mt-5 flex flex-wrap items-center gap-2 text-sm font-medium text-text-muted">
                <span>Gold rate</span>
                <span className="font-semibold text-primary-900">
                  <AedSymbol className="inline h-[0.8em] w-[0.8em]" label="AED " />{results.startRate.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}/g
                </span>
                <span aria-hidden="true">→</span>
                <span className="font-semibold text-primary-900">
                  <AedSymbol className="inline h-[0.8em] w-[0.8em]" label="AED " />{results.currentRate.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}/g today
                </span>
              </div>

            </div>

            <div className="mt-8 max-w-[430px] rounded-card bg-neutral-0 p-5 ring-1 ring-neutral-200 sm:p-6 lg:mt-auto">
              <div className="mb-5 border-b border-neutral-200 pb-5">
                <p className="text-xs font-semibold uppercase tracking-[0.14em] text-primary-500">Rewind your savings to</p>
                <div className="mt-2 flex w-full flex-wrap gap-1 rounded-card bg-primary-50 p-1.5 ring-1 ring-primary-100" role="group" aria-label="Historical start date">
                  {START_OPTIONS.map((option) => {
                    const active = option.key === startKey;
                    return (
                      <button
                        key={option.key}
                        type="button"
                        onClick={() => setStartKey(option.key)}
                        aria-pressed={active}
                        className={buttonVariants({
                          variant: active ? "brand" : "outline-brand",
                          size: "sm",
                          className: cn(
                            "min-w-0 flex-1 rounded-card-sm px-2",
                            !active && "border-2 border-primary-100 bg-neutral-0 hover:border-primary-200 hover:bg-neutral-0"
                          ),
                        })}
                      >
                        {option.label}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div>
                <label htmlFor="cal-monthly" className="text-lg font-medium text-text-muted">Monthly amount saved since {startOption.label}</label>
                <div className="mt-1 flex items-baseline gap-2 text-text-heading">
                  <AedSymbol className="h-[1.05rem] w-[1.05rem]" label="AED " />
                  <span className="font-heading text-[clamp(1.75rem,3vw,2.35rem)] font-semibold leading-none tracking-normal tabular-nums">
                    {monthly.toLocaleString("en-US")}
                  </span>
                  <span className="text-sm text-text-muted">per month</span>
                </div>
              </div>

              <input
                id="cal-monthly"
                type="range"
                min={MIN_AMOUNT}
                max={MAX_AMOUNT}
                step={10}
                value={monthly}
                onChange={handleMonthly}
                className="gold-slider mt-5 w-full cursor-pointer"
                style={{ "--slider-percent": `${monthlyPct}%` } as CSSProperties}
                aria-label="Monthly saving slider"
                aria-valuetext={`${monthly} AED per month`}
              />

            </div>
          </motion.div>

          <motion.div variants={fadeUp(reduce)} className="relative flex min-h-[300px] w-full flex-col justify-end pt-3 lg:ml-auto lg:min-h-[390px] lg:max-w-[560px] lg:pt-10">
            <div className="relative h-[250px] sm:h-[300px] lg:h-[340px]">
              <div className="absolute inset-x-0 top-[25%] border-t border-dashed border-neutral-200" />
              <div className="absolute inset-x-0 top-[60%] border-t border-dashed border-neutral-200" />
              <div className="absolute inset-x-0 bottom-0 border-t border-neutral-300" />
              <div className="pointer-events-none absolute inset-0 z-10">
                {milestones.map((milestone, milestoneIndex) => (
                  <motion.span
                    key={`${years}-${milestone.amount}`}
                    className="absolute flex flex-col items-center gap-1 text-[9px] font-semibold tabular-nums text-text-muted"
                    style={{
                      left: milestone.left,
                      bottom: `calc(${milestone.bottom} + ${milestoneIndex * 7}px)`,
                    }}
                    initial={{ opacity: 0, x: "-50%", y: reduce ? 0 : 8, scale: reduce ? 1 : 0.88 }}
                    animate={{ opacity: 1, x: "-50%", y: 0, scale: 1 }}
                    transition={{ duration: reduce ? 0 : 0.32, delay: reduce ? 0 : milestoneIndex * 0.04, ease: [0.2, 0, 0, 1] }}
                    aria-label={`${milestone.name}, ${milestone.label} historical value`}
                  >
                    <span className="flex h-7 w-7 items-center justify-center rounded-full bg-neutral-0/90 text-base shadow-sm ring-1 ring-neutral-200">
                      {milestone.emoji}
                    </span>
                    <span className="flex flex-col items-center whitespace-nowrap rounded-full bg-neutral-0/90 px-2 py-0.5 text-center shadow-sm ring-1 ring-neutral-200">
                      <span className="text-[9px] font-semibold text-text-heading">{milestone.name}</span>
                      <span className="text-[8px] font-medium text-text-muted">{milestone.label}</span>
                    </span>
                  </motion.span>
                ))}
              </div>
              <div className="chart-bars absolute inset-x-0 bottom-0 flex h-full items-end gap-[5px] sm:gap-2">
                {bars.map((bar, index) => {
                  const totalValue = bar.total;
                  const contributionHeight = `${Math.min(100, (bar.contributed / totalValue) * 100).toPrecision(6)}%`;
                  const growthHeight = `${Math.max(0, (bar.growth / totalValue) * 100).toPrecision(6)}%`;
                  const periodLabel = formatBarPeriod(bar.months);
                  return (
                    <motion.button
                      key={`${years}-${index}`}
                      type="button"
                      className="chart-bar group relative flex min-w-0 flex-1 cursor-default flex-col justify-end gap-[3px] rounded-card-sm outline-none focus-visible:ring-2 focus-visible:ring-primary-600/20"
                      style={{ height: `${((bar.height / (bars[bars.length - 1]?.height || 1)) * 100).toPrecision(6)}%` }}
                      initial={{ scaleY: reduce ? 1 : 0 }}
                      animate={{ scaleY: 1 }}
                      transition={{ duration: 0.6, delay: index * 0.018, ease: [0.2, 0, 0, 1] }}
                      aria-label={`${periodLabel}: ${formatWholeCurrency(totalValue)} total, ${formatShortCurrency(bar.growth)} earned, ${formatShortCurrency(bar.contributed)} added`}
                    >
                      <span className={`pointer-events-none absolute z-30 block w-[176px] translate-y-2 scale-[0.96] rounded-card bg-neutral-0 p-3 text-left opacity-0 shadow-lg ring-1 ring-neutral-200 transition-all duration-300 ease-out group-hover:translate-y-0 group-hover:scale-100 group-hover:opacity-100 group-focus-visible:translate-y-0 group-focus-visible:scale-100 group-focus-visible:opacity-100 ${getTooltipPlacement(index, bars.length, bar.height)}`}>
                        <span className="mb-1.5 flex items-center justify-between gap-3">
                          <span className="text-xs font-medium text-text-muted">Total</span>
                          <span className="text-xs font-semibold text-text-muted">{periodLabel}</span>
                        </span>
                        <span className="mb-3 flex items-baseline gap-1 text-[1.35rem] font-semibold leading-none text-text-heading tabular-nums">
                          <AedSymbol className="h-[0.78em] w-[0.78em]" label="AED " />
                          {formatWholeCurrency(totalValue)}
                        </span>
                        <span className="flex items-center justify-between gap-3">
                          <span className="inline-flex items-center gap-2 text-sm font-semibold text-success">
                            <span className="h-2.5 w-2.5 rounded-full bg-success" />
                            Earned
                          </span>
                          <span className="text-sm font-medium text-text-muted">{formatShortCurrency(bar.growth)}</span>
                        </span>
                        <span className="mt-1.5 flex items-center justify-between gap-3">
                          <span className="chart-added-text inline-flex items-center gap-2 text-sm font-semibold">
                            <span className="chart-added h-2.5 w-2.5 rounded-full" />
                            Added
                          </span>
                          <span className="chart-added-text text-sm font-medium">{formatShortCurrency(bar.contributed)}</span>
                        </span>
                      </span>
                      <div className="bar-segment rounded-t-card-sm bg-success" style={{ height: growthHeight }} />
                      <div className="chart-added bar-segment rounded-b-card-sm" style={{ height: contributionHeight }} />
                    </motion.button>
                  );
                })}
              </div>
            </div>
            <div className="mt-4 flex items-center justify-between text-sm text-text-muted"><span>{startOption.label}</span><span>Today</span></div>
          </motion.div>
        </div>

        <p className="relative mt-4 text-xs leading-relaxed text-text-muted">
          Based on historical gold rates from your chosen start date to today. Gold prices rise and fall. Past performance does not indicate future results, and the value of your savings can go down as well as up.
        </p>
      </div>
      <style jsx>{`
        .chart-bar { transition: transform .26s ease, opacity .26s ease; transform-origin: bottom; }
        .bar-segment { transition: transform .26s ease, opacity .26s ease, filter .26s ease; transform-origin: bottom; }
        .chart-added { background: var(--color-chart-gold); }
        .chart-added-text { color: var(--color-chart-gold); }
        .chart-bars:hover .chart-bar:not(:hover) .bar-segment { opacity: .45; }
        .chart-bar:hover { transform: translateY(-3px); }
        .chart-bar:hover .bar-segment,
        .chart-bar:focus-visible .bar-segment { filter: saturate(1.12); transform: scaleX(1.12); opacity: 1; }
        .gold-slider { -webkit-appearance: none; appearance: none; height: 5px; border-radius: 999px; background: linear-gradient(to right, var(--color-chart-gold) 0%, var(--color-chart-gold) var(--slider-percent), var(--color-neutral-300) var(--slider-percent), var(--color-neutral-300) 100%); outline: none; }
        .gold-slider::-webkit-slider-thumb { -webkit-appearance: none; appearance: none; width: 19px; height: 19px; border-radius: 50%; background: var(--color-neutral-0); border: 3px solid var(--color-chart-gold); box-shadow: var(--shadow-sm); cursor: pointer; transition: transform .15s ease; }
        .gold-slider::-webkit-slider-thumb:hover { transform: scale(1.12); }
        .gold-slider::-moz-range-thumb { width: 19px; height: 19px; border-radius: 50%; background: var(--color-neutral-0); border: 3px solid var(--color-chart-gold); box-shadow: var(--shadow-sm); cursor: pointer; }
        .gold-slider:focus-visible::-webkit-slider-thumb { box-shadow: var(--shadow-focus); }
      `}</style>
    </motion.section>
  );
}
